import { useQuery, useLazyQuery } from '@apollo/client';
import {
  GET_HOMEPAGE_HIGHLIGHTS,
  GET_FEATURED_PACKAGES,
  GET_ALL_PACKAGES,
  GET_PACKAGE_BY_SLUG,
  GET_ALL_DESTINATIONS,
  GET_DESTINATION_BY_SLUG,
  GET_POSTS,
  GET_POST_BY_SLUG,
  GET_REGIONS_AND_COUNTRIES,
  GET_TRAVEL_EVENTS,
  GET_ALL_ACCOMMODATIONS,
  GET_ACCOMMODATION_BY_SLUG,
} from '../lib/queries';
import {
  HomepageHighlightsResponse,
  FeaturedPackagesResponse,
  AllPackagesResponse,
  PackageBySlugResponse,
  AllDestinationsResponse,
  DestinationBySlugResponse,
  PostsResponse,
  PostBySlugResponse,
  RegionsAndCountriesResponse,
  TravelEventsResponse,
  AllAccommodationsResponse,
  AccommodationBySlugResponse,
} from '../types/cms';

// Hook para obtener los destacados de la página principal
export const useHomepageHighlights = () => {
  return useQuery<HomepageHighlightsResponse>(GET_HOMEPAGE_HIGHLIGHTS);
};

// Hook para obtener paquetes destacados
export const useFeaturedPackages = () => {
  return useQuery<FeaturedPackagesResponse>(GET_FEATURED_PACKAGES);
};

// Hook para obtener todos los paquetes
export const useAllPackages = () => {
  return useQuery<AllPackagesResponse>(GET_ALL_PACKAGES);
};

// Hook para obtener un paquete específico por slug
export const usePackageBySlug = (slug: string) => {
  return useQuery<PackageBySlugResponse>(GET_PACKAGE_BY_SLUG, {
    variables: { slug },
    skip: !slug,
  });
};

// Hook lazy para obtener un paquete específico por slug
export const useLazyPackageBySlug = () => {
  return useLazyQuery<PackageBySlugResponse>(GET_PACKAGE_BY_SLUG);
};

// Hook para obtener todos los destinos
export const useAllDestinations = () => {
  return useQuery<AllDestinationsResponse>(GET_ALL_DESTINATIONS);
};

// Hook para obtener un destino específico por slug
export const useDestinationBySlug = (slug: string) => {
  return useQuery<DestinationBySlugResponse>(GET_DESTINATION_BY_SLUG, {
    variables: { slug },
    skip: !slug,
  });
};

// Hook lazy para obtener un destino específico por slug
export const useLazyDestinationBySlug = () => {
  return useLazyQuery<DestinationBySlugResponse>(GET_DESTINATION_BY_SLUG);
};

// Hook para obtener posts con filtros y paginación
export const usePosts = (variables?: {
  where?: Record<string, unknown>;
  take?: number;
  skip?: number;
}) => {
  return useQuery<PostsResponse>(GET_POSTS, {
    variables,
  });
};

// Hook para obtener un post específico por slug
export const usePostBySlug = (slug: string) => {
  return useQuery<PostBySlugResponse>(GET_POST_BY_SLUG, {
    variables: { slug },
    skip: !slug,
  });
};

// Hook lazy para obtener un post específico por slug
export const useLazyPostBySlug = () => {
  return useLazyQuery<PostBySlugResponse>(GET_POST_BY_SLUG);
};

// Hook para obtener regiones y países
export const useRegionsAndCountries = () => {
  return useQuery<RegionsAndCountriesResponse>(GET_REGIONS_AND_COUNTRIES);
};

// Hook para obtener eventos de viaje
export const useTravelEvents = () => {
  return useQuery<TravelEventsResponse>(GET_TRAVEL_EVENTS);
};

// Hook para obtener todos los alojamientos
export const useAllAccommodations = () => {
  return useQuery<AllAccommodationsResponse>(GET_ALL_ACCOMMODATIONS);
};

// Hook para obtener un alojamiento específico por slug
export const useAccommodationBySlug = (slug: string) => {
  return useQuery<AccommodationBySlugResponse>(GET_ACCOMMODATION_BY_SLUG, {
    variables: { slug },
    skip: !slug,
  });
};

// Hook lazy para obtener un alojamiento específico por slug
export const useLazyAccommodationBySlug = () => {
  return useLazyQuery<AccommodationBySlugResponse>(GET_ACCOMMODATION_BY_SLUG);
}; 